<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="assonaCreateTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="assonaCreateTitle">Reparatur hinzufügen</h5>
          <button type="button" class="btn-close" :disabled="busy" data-bs-dismiss="modal" aria-label="Schließen"></button>
        </div>
        <div class="modal-body">
          <p class="create-intro">
            Legt das Ticket für einen Fall zur Schadensnummer an — so wie es sonst automatisch
            beim Lieferschein-Schritt im Assona-Task passiert.
          </p>
          <label class="form-label" for="assona-create-damage-nr">Schadensnummer</label>
          <div class="input-group">
            <input
              id="assona-create-damage-nr"
              ref="inputEl"
              v-model="damageNumber"
              type="text"
              class="form-control ticket-mono"
              maxlength="64"
              autocomplete="off"
              spellcheck="false"
              :disabled="busy"
              @keydown.enter.prevent="onSearch"
            />
            <button
              type="button"
              class="btn btn-outline-secondary"
              :disabled="busy || damageNumber.trim() === ''"
              @click="onSearch"
            >Suchen</button>
          </div>

          <div v-if="errorMessage" class="error create-error">{{ errorMessage }}</div>

          <!-- Alle Fälle zur Nummer: angelegte ausgegraut mit ihrer R2-Nummer,
               die übrigen wählbar. Die Task-ID steht immer dabei. -->
          <div v-if="cases.length" class="create-cases" role="radiogroup" aria-label="Fall wählen">
            <label
              v-for="c in cases"
              :key="c.task_id"
              class="create-case"
              :class="{ disabled: !c.creatable, selected: selectedTaskId === c.task_id }"
            >
              <input
                v-model="selectedTaskId"
                type="radio"
                class="form-check-input"
                name="assona-create-case"
                :value="c.task_id"
                :disabled="!c.creatable || busy"
              />
              <span class="create-case-main">
                <span class="create-case-head">
                  <span class="create-case-type">{{ c.service_order_type || 'Unbekannt' }}</span>
                  <span class="create-case-task ticket-mono">Task {{ c.task_id }}</span>
                  <span v-if="c.task_created" class="create-case-date">{{ formatDate(c.task_created) }}</span>
                </span>
                <span v-if="c.device" class="create-case-device">{{ c.device }}</span>
                <span v-if="c.service_order_type === 'Reklamation'" class="create-case-device">
                  <template v-if="c.complaint_origin_task_id !== null">
                    zu Task {{ c.complaint_origin_task_id }}<template v-if="c.complaint_origin_ticket_ids.length">
                      ({{ c.complaint_origin_ticket_ids.map(id => `R2-${id}`).join(', ') }})</template>
                  </template>
                  <template v-else>Originalfall unbekannt</template>
                </span>
              </span>
              <span class="create-case-state">
                <template v-if="c.ticket_ids.length">
                  <button
                    v-for="id in c.ticket_ids"
                    :key="id"
                    type="button"
                    class="btn btn-link btn-sm create-case-link"
                    @click.prevent="onOpenExisting(id)"
                  >R2-{{ id }}</button>
                </template>
                <span v-else-if="c.blocked_reason === 'service_order_type_not_allowed'">wird nicht übernommen</span>
                <span v-else class="create-case-free">noch kein R2-Fall</span>
              </span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" :disabled="busy" data-bs-dismiss="modal">Abbrechen</button>
          <button
            v-if="cases.length"
            type="button"
            class="btn btn-primary"
            :disabled="busy || selectedTaskId === null"
            @click="onCreate"
          >
            {{ busy ? 'Wird angelegt …' : 'Als R2-Fall anlegen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import { ApiError, createAssona24h, listAssona24hCases, type Assona24hCase } from '../services/tickets';

/**
 * „Reparatur hinzufügen": Schadensnummer suchen, Fall wählen, Ticket anlegen.
 *
 * Zu einer Schadensnummer gibt es meist den Austausch und ggf. eine Reklamation —
 * beide werden gelistet. Fälle mit Ticket sind ausgegraut und verlinken ihre
 * R2-Nummer; wählbar ist nur, was der Server als `creatable` meldet.
 *
 * `open()` löst mit der Ticket-ID auf — der neu angelegten oder einer vorhandenen,
 * die der Benutzer aus der Liste öffnet — und mit `null`, wenn abgebrochen.
 * Aufgelöst wird erst im `hidden`-Event, damit die Navigation keinen Backdrop übernimmt.
 */

const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
const inputEl = useTemplateRef<HTMLInputElement>('inputEl');
let   modal: Modal | null = null;
let   resolver: ((ticketId: number | null) => void) | null = null;
let   ergebnis: number | null = null;

const damageNumber   = ref('');
/** Schadensnummer, zu der `cases` gehört — die Eingabe kann sich danach ändern. */
const searchedNumber = ref('');
const cases          = ref<Assona24hCase[]>([]);
const selectedTaskId = ref<number | null>(null);
const busy           = ref(false);
const errorMessage   = ref<string | null>(null);

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
    modalEl.value.addEventListener('shown.bs.modal', focusInput);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modalEl.value?.removeEventListener('shown.bs.modal', focusInput);
  modal?.dispose();
  modal = null;
});

function open(): Promise<number | null> {
  damageNumber.value   = '';
  searchedNumber.value = '';
  cases.value          = [];
  selectedTaskId.value = null;
  busy.value           = false;
  errorMessage.value   = null;
  ergebnis             = null;
  return new Promise(resolve => {
    resolver = resolve;
    modal?.show();
  });
}

function focusInput(): void {
  void nextTick(() => inputEl.value?.focus());
}

async function onSearch(): Promise<void> {
  const nr = damageNumber.value.trim();
  if (nr === '' || busy.value) return;
  busy.value           = true;
  errorMessage.value   = null;
  cases.value          = [];
  selectedTaskId.value = null;
  try {
    const found = await listAssona24hCases(nr);
    searchedNumber.value = nr;
    cases.value          = found;
    if (found.length === 0) {
      errorMessage.value = `Zur Schadensnummer ${nr} gibt es keinen Assona-24H-Task.`;
    }
    else {
      // Gibt es genau einen anlegbaren Fall, ist er schon gewählt.
      const creatable = found.filter(c => c.creatable);
      if (creatable.length === 1) selectedTaskId.value = creatable[0].task_id;
      if (creatable.length === 0) {
        errorMessage.value = 'Zu dieser Schadensnummer ist jeder Fall schon als R2-Fall angelegt oder wird nicht übernommen.';
      }
    }
  } catch (e) {
    errorMessage.value = e instanceof ApiError && e.message === 'damage_number_invalid'
      ? 'Bitte eine gültige Schadensnummer eingeben.'
      : `Suche fehlgeschlagen: ${e instanceof Error ? e.message : e}`;
    focusInput();
  } finally {
    busy.value = false;
  }
}

async function onCreate(): Promise<void> {
  const taskId = selectedTaskId.value;
  if (taskId === null || busy.value) return;
  busy.value         = true;
  errorMessage.value = null;
  try {
    const ticket = await createAssona24h(searchedNumber.value, taskId);
    ergebnis = ticket.id;
    modal?.hide();
  } catch (e) {
    errorMessage.value = createErrorMessage(e);
    // Liste neu laden: z. B. hat inzwischen jemand anderer den Fall angelegt.
    busy.value = false;
    damageNumber.value = searchedNumber.value;
    const message = errorMessage.value;
    await onSearch();
    errorMessage.value = message;
  } finally {
    busy.value = false;
  }
}

function onOpenExisting(id: number): void {
  ergebnis = id;
  modal?.hide();
}

function createErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.message) {
      case 'assona_task_not_found':
        return 'Dieser Task gehört nicht (mehr) zur Schadensnummer.';
      case 'ticket_already_exists':
        return 'Zu diesem Fall gibt es inzwischen schon ein R2-Ticket.';
      case 'service_order_type_not_allowed':
        return 'Für diesen Serviceordertyp wird kein R2-Fall angelegt (nur „24h Austausch" und „Reklamation").';
      default:
        return `Anlegen fehlgeschlagen: ${e.message}`;
    }
  }
  return e instanceof Error ? `Anlegen fehlgeschlagen: ${e.message}` : 'Anlegen fehlgeschlagen.';
}

function formatDate(value: string): string {
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

function onHidden(): void {
  resolver?.(ergebnis);
  resolver = null;
}

defineExpose({ open });
</script>

<style scoped>
.create-intro {
  margin-bottom: 0.9rem;
  color: var(--muted, #6c757d);
}

.create-error {
  margin-top: 0.75rem;
}

.create-cases {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 1rem;
}

.create-case {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--border, #dee2e6);
  border-radius: 8px;
  cursor: pointer;
}

.create-case.selected {
  border-color: var(--accent, #f7a600);
  background: var(--accent-soft, rgba(247, 166, 0, 0.10));
}

/* Schon angelegt / nicht übernommen: ausgegraut, aber der R2-Link bleibt klickbar. */
.create-case.disabled {
  cursor: default;
  background: #f1f3f5;
  color: #868e96;
}

.create-case .form-check-input {
  margin: 0;
  flex: 0 0 auto;
}

.create-case-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
}

.create-case-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
}

.create-case-type {
  font-weight: 600;
}

.create-case-task,
.create-case-date {
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
}

.create-case-device {
  font-size: 0.875rem;
  color: var(--muted, #6c757d);
}

.create-case-state {
  flex: 0 0 auto;
  font-size: 0.875rem;
  text-align: right;
}

.create-case-link {
  padding: 0 0.25rem;
  font-weight: 600;
  vertical-align: baseline;
}

.create-case-free {
  color: var(--muted, #6c757d);
}
</style>
